/* eslint-disable jsx-a11y/anchor-is-valid */

import { FC, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { vincularFuncionarioEmpresa } from '../../core/_requests'
import { getUserByDocumento } from '../../../../../auth/core/_requests'
import {
  Button,
  ButtonIcon,
  FormInput,
  FormSelect,
  IOption,
  Label,
  LoadingIndicator,
  useModal,
  VinculoEmpresaCard,
} from '../../../../../../components'
import {
  ButtonType,
  CargoType,
  IconPaths,
  IconSize,
  OptionsHelper,
  Permission,
  showAlert,
  applyApiFieldErrors,
  showError,
  VinculoType,
} from '../../../../../../../_metronic/helpers'
import { useAuth, VinculoDto } from '../../../../../auth'
import { isVinculoMedico } from 'app/modules/auth/core/vinculos'
import { getEstadoByUf } from '../../../../atendimento_medicos/atendimentos-medico-list/core/_requests'
import { FuncionarioDtoAdd } from '../../core/_models'
import { vincularSchema } from './validation/vincularSchema'
import { IDocumento } from '../../../../../../components/Modal/SearchPersonModal'
import { useT } from '../../../../../../../_metronic/i18n/Metronici18n'
import { ConselhosModal } from 'app/modules/apps/admin/profissionais-saude/profissional-saude/components/modal/ConselhosModal'
import { getConselhos } from 'app/modules/apps/admin/profissionais-saude/profissional-saude/core/_requests'
import { UsuarioDtoVinculo } from 'app/modules/apps/admin/usuarios/usuarios-list/core/_models'

/** Cargos com registro em conselho de classe (número, UF e conselho). */
const temRegistro = (cargo?: CargoType) =>
  cargo === CargoType.Medico || cargo === CargoType.Solicitante

type Props = {
  documento: IDocumento
  usuario: UsuarioDtoVinculo
}

export const FuncionarioVincularModal: FC<Props> = ({ documento, usuario }) => {
  const { currentUser } = useAuth()
  const { closeModal, showModal } = useModal()
  const { t } = useT()

  const [vinculos, setVinculos] = useState<VinculoDto[]>(usuario.vinculos || [])

  const isAdmin = currentUser?.grupo?.nome === Permission.SuperAdmin
  const medicoVinculo = vinculos.find(isVinculoMedico)

  const initialValues = {
    crm: '',
    uf: undefined as IOption<string> | undefined,
    cargo: undefined as IOption<CargoType> | undefined,
    vinculo: undefined as IOption<VinculoType> | undefined,
    empresa: undefined as IOption<number> | undefined,
    email: usuario.email || '',
    conselho: medicoVinculo?.conselho
      ? {
          label: `${medicoVinculo.conselho.sigla} - ${medicoVinculo.conselho.nome}`,
          value: medicoVinculo.conselho.id,
        }
      : undefined,
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => vincularSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)

      try {
        const funcionario: FuncionarioDtoAdd = {
          cargo: values.cargo?.value as CargoType,
          tipoDocumento: documento.tipo,
          numeroDocumento: documento.numero,
          responsavel:
            values.cargo?.value === CargoType.Responsavel ||
            values.cargo?.value === CargoType.Administrador,
          crm: temRegistro(values.cargo?.value) ? values.crm || undefined : undefined,
          uf: temRegistro(values.cargo?.value) ? values.uf?.value : undefined,
          email: values.email,
          vinculo: values.vinculo ? values.vinculo.value : undefined,
          conselho: values.conselho?.value,
        }
        await vincularFuncionarioEmpresa(values.empresa?.value as number, funcionario)
        const data = await getUserByDocumento(documento.tipo, documento.numero)
        setVinculos(data.vinculos || [])

        showAlert({ title: 'Sucesso!', text: 'Vínculado com sucesso!', icon: 'success' })
      } catch (error) {
        const temOutroErro = applyApiFieldErrors(error, formik.setFieldError, [
          'crm',
          'uf',
          'email',
        ])
        if (temOutroErro) showError(error)
      }
    },
  })

  const { setFieldValue } = formik
  const crmMedico = medicoVinculo?.crm

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (crmMedico !== undefined) {
          const [crm, estado] = crmMedico.split('/')
          const uf = await getEstadoByUf(estado)
          setFieldValue('uf', {
            label: uf.descricao,
            value: uf.sigla,
          })
          setFieldValue('crm', crm)
        }
      } catch (error) {
        showError(error)
      }
    }

    fetchData()
  }, [crmMedico, setFieldValue])

  const openConselhosModal = () => {
    showModal({
      title: 'Gerenciar Conselhos',
      content: <ConselhosModal />,
      fetchers: {
        conselhos: () => getConselhos(),
      },
    })
  }

  // Solicitante pode ter registro em conselho (ex.: enfermagem), mas não é obrigatório: só o
  // número preenchido passa a exigir UF e conselho (ver `vincularSchema`).
  const registroObrigatorio =
    formik.values.cargo?.value === CargoType.Medico ||
    (formik.values.cargo?.value === CargoType.Solicitante && !!formik.values.crm)

  const showVinculo =
    formik.values.cargo?.value === CargoType.Medico &&
    (currentUser?.grupo?.nome === Permission.SuperAdmin ||
      currentUser?.grupo?.nome === Permission.SuperMedico)

  return (
    <>
      <div className='mb-10 bg-light-success p-5 rounded'>
        <div className='text-success'>Funcionário localizado na base de dados.</div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='row mb-10'>
          <Label>Nome: {usuario.nome}</Label>
        </div>
        <div className='row mb-10'>
          <Label>Documento: {documento.numero}</Label>
        </div>

        <h3 className='mb-3'>Vínculos {t('empresa.plural')}</h3>

        <VinculoEmpresaCard vinculos={vinculos} cpf={usuario.cpf} key={1} />

        {!usuario.email && (
          <FormInput
            formik={formik}
            label='E-mail'
            name='email'
            placeholder='Digite o e-mail'
            className='mb-3'
          />
        )}

        <FormSelect
          formik={formik}
          label='Cargo'
          name='cargo'
          placeholder='Selecione um cargo'
          fetchOptions={OptionsHelper.getOptionsCargos}
          className='mb-3'
          isRequired
        />

        {temRegistro(formik.values.cargo?.value) && (
          <>
            <div className='row mb-3'>
              <div className='col-md-6'>
                <FormInput
                  formik={formik}
                  label='Registro no conselho'
                  name='crm'
                  placeholder='Digite o número'
                  isRequired={registroObrigatorio}
                />
              </div>
              <div className='col-md-6'>
                <FormSelect
                  formik={formik}
                  label='UF do Conselho de Classe'
                  name='uf'
                  placeholder='Selecione um cargo'
                  fetchOptions={OptionsHelper.getOptionsEstadosUf}
                  isRequired={registroObrigatorio}
                />
              </div>
            </div>
            <div className='d-flex align-items-end mb-3'>
              <FormSelect
                formik={formik}
                name='conselho'
                label='Conselho'
                fetchOptions={OptionsHelper.getOptionsConselhos}
                className='flex-grow-1'
                isDisabled={formik.isSubmitting}
                placeholder='Selecione um conselho'
                isRequired={registroObrigatorio}
              />
              {isAdmin && (
                <ButtonIcon
                  icon={IconPaths.Gear}
                  iconSize={IconSize.Small}
                  onClick={openConselhosModal}
                  disabled={formik.isSubmitting}
                />
              )}
            </div>
          </>
        )}
        <FormSelect
          formik={formik}
          entity='empresa'
          name='empresa'
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          className='mb-3'
          isDisabled={formik.isSubmitting}
          isRequired
        />

        {showVinculo && (
          <FormSelect
            formik={formik}
            label='Vínculo'
            name='vinculo'
            placeholder='Selecione um vínculo'
            fetchOptions={OptionsHelper.getOptionsTiposVinculos}
            isDisabled={formik.isSubmitting}
          />
        )}

        <div className='d-flex justify-content-center mt-10'>
          <Button
            type={ButtonType.Reset}
            className='btn-secondary me-3'
            data-bs-dismiss='modal'
            onClick={closeModal}
          >
            {t('common.cancel')}
          </Button>
          <Button type={ButtonType.Submit} className='btn-primary'>
            {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
          </Button>
        </div>
      </form>
    </>
  )
}
